/**
 * Module dependencies.
 */

import { Radio } from 'src/components/core';
import { ReactElement, useCallback, useState } from 'react';
import { StepPageConfig } from 'src/types/advisor';
import { units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import BaseStep from './base-step';
import ContactFormStep from './contact-form';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = StepPageConfig & {
  className?: string;
  ignoreFallback?: boolean;
  translateOptions?: boolean;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

/**
 * `OptionsWrapper` styled component.
 */

const OptionsWrapper = styled.div`
  display: grid;
  grid-auto-columns: 100%;
  grid-row-gap: ${units(3)};
  margin-bottom: ${units(6)};
`;

/**
 * `StyledRadio` styled component.
 */

const StyledRadio = styled(Radio)`
  font-weight: 700;
`;

/**
 * `StepRadioOptions` component.
 */

const StepRadioOptions = (props: Props): ReactElement => {
  const {
    className,
    filterKey,
    ignoreFallback,
    options,
    pageIndex,
    pageKey,
    translateOptions = true
  } = props;

  const {
    filteredModelRanges,
    getCheckedValue,
    isSuccessModelRanges,
    onGoToNext,
    onGoToPrevious,
    onSetFilter,
    refetch
  } = useAdvisor();

  const { translate } = useTranslate();
  const checkedValue = getCheckedValue(pageKey);
  const [value, setValue] = useState<string | null>(checkedValue);
  const handleChange = useCallback(
    ({ target }) => {
      setValue(target.value);

      if (filterKey) {
        onSetFilter(filterKey, JSON.stringify(target.value));
        refetch();
      }

      onGoToNext(target.value, pageKey, pageIndex);
    },
    [filterKey, onGoToNext, onSetFilter, pageIndex, pageKey, refetch]
  );

  const handleContinue = useCallback(() => {
    if (filterKey) {
      onSetFilter(filterKey, JSON.stringify(value));
      refetch();
    }

    refetch();
    onGoToNext(value, pageKey, pageIndex);
  }, [filterKey, onGoToNext, onSetFilter, pageIndex, pageKey, refetch, value]);

  if (isSuccessModelRanges && isEmpty(filteredModelRanges) && !ignoreFallback) {
    return <ContactFormStep />;
  }

  return (
    <Wrapper className={className}>
      <BaseStep
        canContinue={!isEmpty(checkedValue)}
        onContinue={handleContinue}
        onGoBack={() => onGoToPrevious(pageIndex)}
        pageKey={pageKey}
        {...props}
      >
        <OptionsWrapper>
          {map(options, (key, index) => (
            <StyledRadio
              checked={checkedValue && `${checkedValue}` === `${key}`}
              key={index}
              label={
                translateOptions
                  ? translate(`advisor:steps.${pageKey}.options.${key}`)
                  : key
              }
              name={`advisor-${pageKey}`}
              onChange={handleChange}
              value={key}
            />
          ))}
        </OptionsWrapper>
      </BaseStep>
    </Wrapper>
  );
};

/**
 * Export `StepRadioOptions` component.
 */

export default StepRadioOptions;
